import { create } from "zustand";
import { ParsedCompanyDetails } from "../types/ExcelParsingOnboarding";
import {
  CaptableAiNotification,
  CaptableAIBatchStatus,
  getActiveCaptableAIBatches,
  getCaptableAIBatchStatus,
  getCaptableAINotifications,
  markCaptableAINotificationRead,
  retryCaptableAIFile,
} from "../api/companyExcelOnboarding";

export type BackgroundJobConnection =
  | "idle"
  | "loading"
  | "connected"
  | "reconnecting"
  | "recovered"
  | "failed";

export type CaptableAiBatchContext = {
  flow?: "captable" | "company-onboarding";
  companyId?: string;
  companyName?: string;
  returnTo?: string;
  filenames?: string[];
  files?: Array<{ filename: string; size: number }>;
  sourceFiles?: File[];
  draftCompanyDetails?: Partial<ParsedCompanyDetails>;
};

const TERMINAL = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"];
const CACHE_KEY = "captable-ai-batch-ids";
const CONTEXT_CACHE_KEY = "captable-ai-batch-contexts";
const DISMISSED_NOTIFICATION_KEY = "captable-ai-dismissed-notification-ids";
const DISMISSED_BATCH_KEY = "captable-ai-dismissed-batch-ids";
const READ_PROCESSING_BATCH_KEY = "captable-ai-read-processing-batch-ids";

const readProcessingBatchIds = () => {
  try {
    return (
      JSON.parse(
        sessionStorage.getItem(READ_PROCESSING_BATCH_KEY) || "[]"
      ) as string[]
    ).reduce<Record<string, true>>((map, batchId) => {
      map[batchId] = true;
      return map;
    }, {});
  } catch (_) {
    return {};
  }
};

const rememberReadProcessingBatchIds = (ids: Record<string, true>) => {
  try {
    sessionStorage.setItem(
      READ_PROCESSING_BATCH_KEY,
      JSON.stringify(Object.keys(ids))
    );
  } catch (_) {
    // In-memory read state remains available when browser storage is disabled.
  }
};

const cachedIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "[]") as string[];
  } catch (_) {
    return [];
  }
};

const remember = (ids: string[]) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch (_) {
    // Storage can be unavailable in private browsing; the in-memory cache still works.
  }
};

const cachedContexts = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(CONTEXT_CACHE_KEY) || "{}"
    ) as Record<string, CaptableAiBatchContext>;
  } catch (_) {
    return {};
  }
};

const rememberContexts = (contexts: Record<string, CaptableAiBatchContext>) => {
  try {
    sessionStorage.setItem(CONTEXT_CACHE_KEY, JSON.stringify(contexts));
  } catch (_) {
    // In-memory context remains available when browser storage is disabled.
  }
};

const dismissedNotificationIds = () => {
  try {
    return new Set<string>(
      JSON.parse(sessionStorage.getItem(DISMISSED_NOTIFICATION_KEY) || "[]")
    );
  } catch (_) {
    return new Set<string>();
  }
};

const rememberDismissedNotificationIds = (ids: Set<string>) => {
  try {
    sessionStorage.setItem(
      DISMISSED_NOTIFICATION_KEY,
      JSON.stringify(Array.from(ids))
    );
  } catch (_) {
    // Dismissal still applies to the current in-memory session.
  }
};

const dismissedBatchIds = () => {
  try {
    return new Set<string>(
      JSON.parse(sessionStorage.getItem(DISMISSED_BATCH_KEY) || "[]")
    );
  } catch (_) {
    return new Set<string>();
  }
};

const rememberDismissedBatchIds = (ids: Set<string>) => {
  try {
    sessionStorage.setItem(
      DISMISSED_BATCH_KEY,
      JSON.stringify(Array.from(ids))
    );
  } catch (_) {
    // Dismissal still applies to the current in-memory session.
  }
};

export const isTerminalCaptableBatch = (batch?: CaptableAIBatchStatus) =>
  Boolean(batch?.status && TERMINAL.includes(batch.status));

interface BackgroundJobsState {
  batches: Record<string, CaptableAIBatchStatus>;
  batchContexts: Record<string, CaptableAiBatchContext>;
  focusedBatchId?: string;
  clearedBatchId?: string;
  notifications: Record<string, CaptableAiNotification>;
  unreadCount: number;
  readProcessingBatches: Record<string, true>;
  notificationStreamConnected: boolean;
  connection: Record<string, BackgroundJobConnection>;
  loading: boolean;
  initialized: boolean;
  retrying: Record<string, boolean>;
  initialize: () => Promise<void>;
  fetchNotifications: (unread?: boolean) => Promise<void>;
  applyNotification: (notification: CaptableAiNotification) => boolean;
  setNotificationStreamConnected: (connected: boolean) => void;
  focusBatch: (batchId?: string) => void;
  registerBatch: (
    batchId: string,
    seed?: CaptableAIBatchStatus,
    context?: CaptableAiBatchContext
  ) => Promise<void>;
  refreshBatch: (batchId: string) => Promise<CaptableAIBatchStatus>;
  applyBatch: (batch: CaptableAIBatchStatus) => void;
  setConnection: (batchId: string, value: BackgroundJobConnection) => void;
  markRead: (notificationId: string) => Promise<void>;
  markProcessingRead: (batchId: string) => void;
  dismissNotification: (
    notificationId: string,
    clearBatch?: boolean
  ) => Promise<void>;
  dismissAllNotifications: (clearBatches?: boolean) => Promise<void>;
  forgetBatch: (batchId: string) => void;
  clearClearedBatch: () => void;
  retryFile: (batchId: string, fileId: string) => Promise<void>;
  clear: () => void;
}

export const useBackgroundJobsStore = create<BackgroundJobsState>(
  (set, get) => ({
    batches: {},
    batchContexts: {},
    focusedBatchId: undefined,
    clearedBatchId: undefined,
    notifications: {},
    unreadCount: 0,
    readProcessingBatches: readProcessingBatchIds(),
    notificationStreamConnected: false,
    connection: {},
    loading: false,
    initialized: false,
    retrying: {},
    initialize: async () => {
      if (get().loading) return;
      set({ loading: true });
      try {
        const active = await getActiveCaptableAIBatches();
        const activeIds = active.map((batch) => batch.batchId);
        const remembered = cachedIds().filter((id) => !activeIds.includes(id));
        const recovered = await Promise.all(
          remembered.map((id) =>
            getCaptableAIBatchStatus(id).catch(() => undefined)
          )
        );
        const all = [
          ...active,
          ...recovered.filter(Boolean),
        ] as CaptableAIBatchStatus[];
        const batches = all.reduce<Record<string, CaptableAIBatchStatus>>(
          (map, batch) => {
            map[batch.batchId] = batch;
            return map;
          },
          {}
        );
        set({ batches, batchContexts: cachedContexts() });
        await get().fetchNotifications();
        const notificationBatchIds = Object.values(get().notifications).map(
          (notification) => notification.batchId
        );
        const notificationBatches = await Promise.all(
          notificationBatchIds.map((batchId) =>
            getCaptableAIBatchStatus(batchId).catch(() => undefined)
          )
        );
        notificationBatches.forEach((batch) => {
          if (batch) get().applyBatch(batch);
        });
        set({ initialized: true, loading: false });
        remember(
          all
            .filter((batch) => !isTerminalCaptableBatch(batch))
            .map((batch) => batch.batchId)
        );
      } catch (_) {
        set({ loading: false, initialized: true });
      }
    },
    fetchNotifications: async (unread) => {
      const incoming = await getCaptableAINotifications(unread);
      set((state) => {
        const dismissed = dismissedNotificationIds();
        const dismissedBatches = dismissedBatchIds();
        const source = unread
          ? [...Object.values(state.notifications), ...incoming]
          : incoming;
        const byBatch = source.reduce<Record<string, CaptableAiNotification>>(
          (map, notification) => {
            if (
              dismissed.has(notification.id) ||
              dismissedBatches.has(notification.batchId)
            )
              return map;
            const current = map[notification.batchId];
            if (
              !current ||
              new Date(notification.createdAt).getTime() >
                new Date(current.createdAt).getTime()
            ) {
              map[notification.batchId] = notification;
            }
            return map;
          },
          {}
        );
        const notifications = Object.values(byBatch).reduce<
          Record<string, CaptableAiNotification>
        >((map, notification) => {
          map[notification.id] = notification;
          return map;
        }, {});
        return {
          notifications,
          unreadCount: Object.values(notifications).filter(
            (notification) => !notification.isRead
          ).length,
        };
      });
    },
    applyNotification: (notification) => {
      if (
        !notification?.id ||
        dismissedNotificationIds().has(notification.id) ||
        dismissedBatchIds().has(notification.batchId) ||
        get().notifications[notification.id]
      )
        return false;
      set((state) => {
        const notifications = Object.values(state.notifications).reduce<
          Record<string, CaptableAiNotification>
        >((map, current) => {
          if (current.batchId !== notification.batchId)
            map[current.id] = current;
          return map;
        }, {});
        notifications[notification.id] = notification;
        return {
          notifications,
          unreadCount: Object.values(notifications).filter(
            (item) => !item.isRead
          ).length,
        };
      });
      return true;
    },
    setNotificationStreamConnected: (connected) =>
      set({ notificationStreamConnected: connected }),
    markProcessingRead: (batchId) =>
      set((state) => {
        const readProcessingBatches = {
          ...state.readProcessingBatches,
          [batchId]: true as const,
        };
        rememberReadProcessingBatchIds(readProcessingBatches);
        return { readProcessingBatches };
      }),
    focusBatch: (batchId) => set({ focusedBatchId: batchId }),
    forgetBatch: (batchId) => {
      const ids = cachedIds().filter((id) => id !== batchId);
      const contexts = cachedContexts();
      delete contexts[batchId];
      remember(ids);
      rememberContexts(contexts);
      set((state) => {
        const batches = { ...state.batches };
        const batchContexts = { ...state.batchContexts };
        delete batches[batchId];
        delete batchContexts[batchId];
        return {
          batches,
          batchContexts,
          focusedBatchId:
            state.focusedBatchId === batchId ? undefined : state.focusedBatchId,
          clearedBatchId: batchId,
        };
      });
    },
    clearClearedBatch: () => set({ clearedBatchId: undefined }),
    registerBatch: async (batchId, seed, context) => {
      remember([...cachedIds(), batchId]);
      if (context) {
        set((state) => ({
          batchContexts: {
            ...state.batchContexts,
            [batchId]: context,
          },
        }));
        rememberContexts({ ...cachedContexts(), [batchId]: context });
      }
      if (seed) get().applyBatch(seed);
      else await get().refreshBatch(batchId);
    },
    refreshBatch: async (batchId) => {
      const batch = await getCaptableAIBatchStatus(batchId);
      get().applyBatch(batch);
      return batch;
    },
    applyBatch: (batch) => {
      if (!batch?.batchId) return;
      set((state) => ({
        batches: { ...state.batches, [batch.batchId]: batch },
      }));
      remember([...cachedIds(), batch.batchId]);
    },
    setConnection: (batchId, value) =>
      set((state) => ({
        connection: { ...state.connection, [batchId]: value },
      })),
    markRead: async (notificationId) => {
      await markCaptableAINotificationRead(notificationId);
      set((state) => {
        const notification = state.notifications[notificationId];
        if (!notification) return state;
        const notifications = {
          ...state.notifications,
          [notificationId]: { ...notification, isRead: true },
        };
        return {
          notifications,
          unreadCount: Object.values(notifications).filter(
            (item) => !item.isRead
          ).length,
        };
      });
    },
    dismissNotification: async (notificationId, clearBatch = false) => {
      await markCaptableAINotificationRead(notificationId);
      const batchId = get().notifications[notificationId]?.batchId;
      const dismissed = dismissedNotificationIds();
      const dismissedBatches = dismissedBatchIds();
      dismissed.add(notificationId);
      if (batchId) dismissedBatches.add(batchId);
      rememberDismissedNotificationIds(dismissed);
      rememberDismissedBatchIds(dismissedBatches);
      set((state) => {
        const notifications = { ...state.notifications };
        delete notifications[notificationId];
        return {
          notifications,
          unreadCount: Object.values(notifications).filter(
            (item) => !item.isRead
          ).length,
        };
      });
      if (batchId && clearBatch) get().forgetBatch(batchId);
    },
    dismissAllNotifications: async (clearBatches = false) => {
      const current = Object.values(get().notifications);
      const results = await Promise.allSettled(
        current.map(async (notification) => {
          if (!notification.isRead) {
            await markCaptableAINotificationRead(notification.id);
          }
          return notification;
        })
      );
      const successfulNotifications = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : []
      );
      const successful = successfulNotifications.map(
        (notification) => notification.id
      );
      const successfulBatchIds = successfulNotifications.map(
        (notification) => notification.batchId
      );
      const dismissed = dismissedNotificationIds();
      const dismissedBatches = dismissedBatchIds();
      successful.forEach((id) => dismissed.add(id));
      successfulBatchIds.forEach((batchId) => dismissedBatches.add(batchId));
      rememberDismissedNotificationIds(dismissed);
      rememberDismissedBatchIds(dismissedBatches);
      set((state) => {
        const notifications = { ...state.notifications };
        successful.forEach((id) => delete notifications[id]);
        return {
          notifications,
          unreadCount: Object.values(notifications).filter(
            (notification) => !notification.isRead
          ).length,
        };
      });
      if (clearBatches) {
        successfulBatchIds.forEach((batchId) => get().forgetBatch(batchId));
      }
    },
    retryFile: async (batchId, fileId) => {
      const key = `${batchId}:${fileId}`;
      if (get().retrying[key]) return;
      set((state) => ({ retrying: { ...state.retrying, [key]: true } }));
      try {
        // A retry starts a new notification lifecycle for this batch.
        const dismissedBatches = dismissedBatchIds();
        dismissedBatches.delete(batchId);
        rememberDismissedBatchIds(dismissedBatches);
        const batch = await retryCaptableAIFile(batchId, fileId);
        const cached = get().batches[batchId];
        const current = batch?.batchId
          ? batch
          : {
              ...cached,
              batchId,
              files: (cached?.files || []).map((file) =>
                (file.fileId || file.id) === fileId
                  ? {
                      ...file,
                      status: "QUEUED",
                      stage: "QUEUED",
                      error: undefined,
                      errorMessage: undefined,
                      errorStage: undefined,
                    }
                  : file
              ),
            };
        get().applyBatch({
          ...current,
          status: isTerminalCaptableBatch(current)
            ? "RETRY_QUEUED"
            : current.status,
        });
        set((state) => {
          const notifications = Object.values(state.notifications).reduce<
            Record<string, CaptableAiNotification>
          >((map, notification) => {
            if (notification.batchId !== batchId)
              map[notification.id] = notification;
            return map;
          }, {});
          const readProcessingBatches = { ...state.readProcessingBatches };
          delete readProcessingBatches[batchId];
          rememberReadProcessingBatchIds(readProcessingBatches);
          return {
            notifications,
            readProcessingBatches,
            unreadCount: Object.values(notifications).filter(
              (item) => !item.isRead
            ).length,
          };
        });
      } finally {
        set((state) => ({ retrying: { ...state.retrying, [key]: false } }));
      }
    },
    clear: () =>
      set({
        batches: {},
        batchContexts: {},
        focusedBatchId: undefined,
        clearedBatchId: undefined,
        notifications: {},
        unreadCount: 0,
        readProcessingBatches: readProcessingBatchIds(),
        notificationStreamConnected: false,
        connection: {},
        retrying: {},
        loading: false,
        initialized: false,
      }),
  })
);
